import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Table,
  Badge,
  Modal,
  ListGroup,
  InputGroup,
} from "react-bootstrap";
import Select from "react-select";
import {
  Plus,
  Calendar,
  ListChecks,
  Lock,
  CheckCircle,
  RotateCcw,
  Trash2,
  Edit2,
  CheckSquare,
  Check,
  X,
  Minus,
} from "lucide-react";
import Layout from "../../layout/Layout";
import { useAuth } from "../../context/AuthContext";

import {
  hygienePlanList,
  hygienePlanInsert,
  hygienePlanUpdate,
  hygienePlanDelete,
  hygienePlanDetail,
  hygieneItemCheck,
  hygieneBulkCheck,
  hygienePlanComplete,
  hygienePlanReset,
  hygieneItemAdd,
  hygieneItemUpdate,
  hygieneItemDelete,
  getPersonelForEquipmentPlan,
} from "../../../api/bakim/hygienePlanApi";

const monthsList = [
  { id: 1, name: "OCAK" },
  { id: 2, name: "SUBAT" },
  { id: 3, name: "MART" },
  { id: 4, name: "NISAN" },
  { id: 5, name: "MAYIS" },
  { id: 6, name: "HAZIRAN" },
  { id: 7, name: "TEMMUZ" },
  { id: 8, name: "AGUSTOS" },
  { id: 9, name: "EYLUL" },
  { id: 10, name: "EKIM" },
  { id: 11, name: "KASIM" },
  { id: 12, name: "ARALIK" },
];

function HygienePlanPage() {
  const { user } = useAuth();
  const permissions = user?.permissions || [];

  const canInsert = permissions.includes("idariBakim_create");
  const canUpdate = permissions.includes("idariBakim_update");
  const canDelete = permissions.includes("idariBakim_delete");

  const [plans, setPlans] = useState([]);
  const [personels, setPersonels] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const [showPlanModal, setShowPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  // Sablon Yonetimi State (Admin)
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [activePlanForTemplate, setActivePlanForTemplate] = useState(null);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [templateItems, setTemplateItems] = useState([]);

  // Operasyon Checklist State (Kullanici)
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistItems, setChecklistItems] = useState([]);
  const [activeTask, setActiveTask] = useState({
    planId: null,
    monthId: null,
    status: "planned",
  });

  const initialFormState = {
    title: "",
    operation: "",
    year: new Date().getFullYear(),
    repeatPerYear: "",
    responsibleUsers: [],
    months: [],
  };
  const [formData, setFormData] = useState(initialFormState);

  useEffect(() => {
    fetchPersonels();
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      const res = await hygienePlanList({ year: selectedYear });
      setPlans(res.data.data || []);
    } catch (err) {
      console.error("Plan yukleme hatasi:", err);
    }
  }, [selectedYear]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const fetchPersonels = async () => {
    try {
      const res = await getPersonelForEquipmentPlan();
      setPersonels(res.data.data.map((p) => ({ value: p._id, label: p.name })));
    } catch (err) {
      console.error("Personel yukleme hatasi:", err);
    }
  };

  /* ==================== PLAN İŞLEMLERİ ==================== */

  const handleOpenPlanModal = (plan = null) => {
    if (plan) {
      if (!canUpdate) return;
      setEditingPlan(plan);

      const mappedUsers = (plan.responsibleUsers || []).map((u) => {
        const userId = typeof u === "object" ? u._id : u;
        const match = personels.find((p) => p.value === userId);

        return match
          ? match
          : {
              value: userId,
              label:
                typeof u === "object" && u.name
                  ? u.name
                  : "Bilinmeyen Personel",
            };
      });

      setFormData({
        title: plan.title,
        operation: plan.operation,
        year: plan.year,
        repeatPerYear: plan.repeatPerYear || "",
        responsibleUsers: mappedUsers,
        months: plan.months || [],
      });
    } else {
      if (!canInsert) return;
      setEditingPlan(null);
      setFormData({ ...initialFormState, year: selectedYear });
    }
    setShowPlanModal(true);
  };

  const handleSavePlan = async () => {
    try {
      const cleanedMonths = formData.months.map((m) => ({
        month: m.month,
        status: m.status || "planned",
      }));

      const payload = {
        ...formData,
        repeatPerYear: formData.repeatPerYear
          ? parseInt(formData.repeatPerYear, 10)
          : undefined,
        responsibleUsers: formData.responsibleUsers.map((u) => u.value),
        months: cleanedMonths,
      };

      if (editingPlan) {
        await hygienePlanUpdate({ _id: editingPlan._id, ...payload });
      } else {
        await hygienePlanInsert(payload);
      }

      setShowPlanModal(false);
      fetchPlans();
    } catch (err) {
      alert("Plan isleminde bir hata olustu");
      console.error(err);
    }
  };

  const handleDeletePlan = async (id) => {
    if (!canDelete) return;

    if (window.confirm("Bu plani silmek istediginize emin misiniz?")) {
      try {
        await hygienePlanDelete({ planId: id });
        fetchPlans();
      } catch (err) {
        const message =
          err?.response?.data?.data?.message ||
          err?.response?.data?.message ||
          "Silme islemi basarisiz oldu.";

        alert(message);
      }
    }
  };

  /* ==================== ŞABLON (TEMPLATE) İŞLEMLERİ ==================== */

  const handleOpenTemplateModal = async (plan) => {
    setActivePlanForTemplate(plan);
    try {
      const currentMonth = new Date().getMonth() + 1;
      const res = await hygienePlanDetail(plan._id, currentMonth);
      setTemplateItems(res.data.data.items || []);
      setShowTemplateModal(true);
    } catch (err) {
      console.error("Sablon detaylari cekilemedi");
    }
  };

  const handleAddItem = async () => {
    if (!newItemTitle.trim()) return;
    try {
      await hygieneItemAdd({
        planId: activePlanForTemplate._id,
        title: newItemTitle,
        order: templateItems.length + 1,
      });
      setNewItemTitle("");
      handleOpenTemplateModal(activePlanForTemplate);
    } catch (err) {
      alert("Kalem eklenemedi");
    }
  };

  const handleEditItem = async (item) => {
    const newTitle = window.prompt("Yeni kalem adini giriniz:", item.title);
    if (!newTitle || newTitle.trim() === item.title) return;

    try {
      await hygieneItemUpdate({
        planId: activePlanForTemplate._id,
        itemId: item._id,
        title: newTitle,
      });
      handleOpenTemplateModal(activePlanForTemplate);
    } catch (err) {
      alert("Kalem guncellenemedi");
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm("Bu kalemi silmek istediginize emin misiniz?")) {
      try {
        await hygieneItemDelete({
          planId: activePlanForTemplate._id,
          itemId: itemId,
        });
        handleOpenTemplateModal(activePlanForTemplate);
      } catch (err) {
        alert("Kalem silinemedi");
      }
    }
  };

  /* ==================== CHECKLIST İŞLEMLERİ ==================== */

  const getMonthData = (plan, monthId) => {
    return plan?.months?.find((m) => m.month === monthId);
  };

  const handleMonthCellClick = async (plan, monthId) => {
    if (!canUpdate) return;

    const existingMonth = getMonthData(plan, monthId);
    const currentStatus = existingMonth ? existingMonth.status : "planned";

    setActiveTask({
      planId: plan._id,
      monthId: monthId,
      status: currentStatus,
    });

    try {
      const res = await hygienePlanDetail(plan._id, monthId);
      setChecklistItems(res.data.data.items || []);

      if (res.data.planMonthStatus) {
        setActiveTask((prev) => ({
          ...prev,
          status: res.data.planMonthStatus,
        }));
      }

      setShowChecklistModal(true);
    } catch (err) {
      console.error("Checklist cekilemedi", err);
      alert("Liste yuklenirken bir hata olustu.");
    }
  };

  const handleStatusChange = async (itemId, newStatus) => {
    setChecklistItems((prev) =>
      prev.map((item) =>
        item._id === itemId ? { ...item, status: newStatus } : item
      )
    );

    try {
      await hygieneItemCheck({
        planId: activeTask.planId,
        itemId: itemId,
        month: activeTask.monthId,
        status: newStatus,
      });
    } catch (err) {
      alert("Isaretleme kaydedilemedi!");
      const res = await hygienePlanDetail(
        activeTask.planId,
        activeTask.monthId
      );
      setChecklistItems(res.data.data.items || []);
    }
  };

  const handleBulkCheck = async () => {
    try {
      // Ekranda listelenen tüm checklist item'larının ID'lerini bir diziye aktarıyoruz
      const itemIds = checklistItems.map((item) => item._id);

      await hygieneBulkCheck({
        planId: activeTask.planId,
        month: activeTask.monthId,
        itemIds: itemIds, // Backend'in beklediği array eklendi
        status: "checked",
      });

      const res = await hygienePlanDetail(
        activeTask.planId,
        activeTask.monthId
      );
      setChecklistItems(res.data.data.items || []);
    } catch (err) {
      alert("Toplu isaretleme basarisiz oldu.");
    }
  };

  const handleComplete = async () => {
    try {
      await hygienePlanComplete({
        planId: activeTask.planId,
        month: activeTask.monthId,
      });
      const res = await hygienePlanDetail(
        activeTask.planId,
        activeTask.monthId
      );
      setChecklistItems(res.data.data.items || []);
      setActiveTask((prev) => ({ ...prev, status: "done" }));
      fetchPlans();
    } catch (err) {
      alert(err.response?.data?.message || "Plan tamamlanirken hata olustu.");
    }
  };

  const handleReset = async () => {
    if (
      window.confirm(
        "Bu ayin tum kayitlarini sifirlamak istediginize emin misiniz?"
      )
    ) {
      try {
        await hygienePlanReset({
          planId: activeTask.planId,
          month: activeTask.monthId,
        });
        const res = await hygienePlanDetail(
          activeTask.planId,
          activeTask.monthId
        );
        setChecklistItems(res.data.data.items || []);
        setActiveTask((prev) => ({ ...prev, status: "planned" }));
        fetchPlans();
      } catch (err) {
        alert("Sifirlama basarisiz.");
      }
    }
  };

  // İSTATİSTİK HESAPLAMALARI
  const checkedCount = checklistItems.filter(
    (x) => x.status === "checked"
  ).length;
  const notDoneCount = checklistItems.filter(
    (x) => x.status === "not_done"
  ).length;
  const naCount = checklistItems.filter(
    (x) => x.status === "not_applicable"
  ).length;

  const renderMonthBox = (plan, monthId) => {
    const mData = getMonthData(plan, monthId);
    let boxClass = "month-box-empty";
    let text = "";
    let dateText = "";

    if (mData) {
      if (mData.status === "planned") {
        boxClass = "month-box-planned";
        text = "P";
      } else if (mData.status === "done") {
        boxClass = "month-box-done";
        text = "G";
        if (mData.doneDate) {
          const d = new Date(mData.doneDate);
          dateText = `${String(d.getDate()).padStart(2, "0")}.${String(
            d.getMonth() + 1
          ).padStart(2, "0")}`;
        }
      } else if (mData.status === "not_done") {
        boxClass = "month-box-not-done";
        text = "X";
      }
    }

    return (
      <div
        onClick={() => handleMonthCellClick(plan, monthId)}
        className={`table-month-indicator flex-column ${boxClass} ${
          !canUpdate ? "cursor-not-allowed" : ""
        }`}
        title={canUpdate ? "Kontrol Listesini Ac" : "Yetkiniz yok"}
      >
        <span style={{ lineHeight: dateText ? "1" : "inherit" }}>{text}</span>
        {dateText && (
          <span
            style={{
              fontSize: "9px",
              marginTop: "2px",
              fontWeight: "normal",
              opacity: "0.9",
            }}
          >
            {dateText}
          </span>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div
        className="p-4"
        style={{ backgroundColor: "#f4f7f6", minHeight: "100vh" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h4 className="fw-bold text-secondary mb-1">
              İDARİ İŞLER AYLIK-YILLIK BAKIM
            </h4>
            <p className="text-muted fw-semibold small mb-0">
              {selectedYear} AYLIK BAZDA PLANLANAN (P) GERÇEKLEŞEN (G)
            </p>
          </div>
          <div className="d-flex gap-3 align-items-center">
            <div className="d-flex align-items-center gap-2 bg-white px-3 py-1 border rounded shadow-sm">
              <Calendar size={16} className="text-muted" />
              <Form.Select
                size="sm"
                className="border-0 fw-bold text-dark shadow-none"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                style={{ cursor: "pointer", minWidth: "90px" }}
              >
                {[...Array(5)].map((_, i) => {
                  const yr = new Date().getFullYear() - 2 + i;
                  return (
                    <option key={yr} value={yr}>
                      {yr}
                    </option>
                  );
                })}
              </Form.Select>
            </div>

            {canInsert && (
              <Button
                variant="primary"
                className="d-flex align-items-center gap-2 shadow-sm"
                onClick={() => handleOpenPlanModal()}
              >
                <Plus size={18} /> Yeni Plan
              </Button>
            )}
          </div>
        </div>

        <Card className="border-0 shadow-sm border-radius-15">
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0 custom-table align-middle">
              <thead>
                <tr>
                  <th className="text-center" style={{ width: "3%" }}>
                    NO
                  </th>
                  <th className="ps-4" style={{ width: "20%" }}>
                    Lokasyon / Plan
                  </th>
                  <th className="text-center" style={{ width: "6%" }}>
                    Tekrar
                  </th>
                  <th style={{ width: "12%" }}>Sorumlu</th>
                  {monthsList.map((m) => (
                    <th
                      key={m.id}
                      className="text-center"
                      style={{ width: "3%" }}
                    >
                      {m.name}
                    </th>
                  ))}
                  {(canUpdate || canDelete) && (
                    <th className="text-center" style={{ width: "12%" }}>
                      YÖNET
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {plans.length === 0 ? (
                  <tr>
                    <td
                      colSpan={canUpdate || canDelete ? "17" : "16"}
                      className="text-center py-4 text-muted"
                    >
                      Seçili yıla ait plan bulunamadı.
                    </td>
                  </tr>
                ) : (
                  plans.map((plan, index) => (
                    <tr key={plan._id}>
                      <td className="text-center text-muted fw-bold">
                        {index + 1}
                      </td>
                      <td className="ps-4 py-3">
                        <div className="fw-bold text-dark">{plan.title}</div>
                        <div className="text-muted small">{plan.operation}</div>
                      </td>
                      <td className="text-center fw-medium text-secondary">
                        {plan.repeatPerYear ? `${plan.repeatPerYear}/YIL` : "-"}
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {plan.responsibleUsers?.slice(0, 2).map((u, i) => (
                            <Badge
                              key={i}
                              bg="light"
                              text="dark"
                              className="border fw-normal"
                            >
                              {u.name || "Personel"}
                            </Badge>
                          ))}
                        </div>
                      </td>
                      {monthsList.map((m) => (
                        <td key={m.id} className="text-center p-1">
                          {renderMonthBox(plan, m.id)}
                        </td>
                      ))}
                      {(canUpdate || canDelete) && (
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            {canUpdate && (
                              <>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="btn-icon"
                                  title="Şablon Kalemlerini Yönet"
                                  onClick={() => handleOpenTemplateModal(plan)}
                                >
                                  <ListChecks size={14} />
                                </Button>
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  className="btn-icon"
                                  title="Planı Düzenle"
                                  onClick={() => handleOpenPlanModal(plan)}
                                >
                                  <Edit2 size={14} />
                                </Button>
                              </>
                            )}
                            {canDelete && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                className="btn-icon"
                                title="Planı Sil"
                                onClick={() => handleDeletePlan(plan._id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {/* PLAN OLUSTURMA VE GUNCELLEME MODALI */}
        <Modal
          show={showPlanModal}
          onHide={() => setShowPlanModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold">
              {editingPlan ? "Planı Güncelle" : "Yeni Bakım/Temizlik Planı"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                      Lokasyon / Alan Adı
                    </Form.Label>
                    <Form.Control
                      value={formData.title}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          title: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">Yıl</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.year}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          year: parseInt(e.target.value),
                        })
                      }
                      disabled={!!editingPlan}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                      Tekrar (/Yıl)
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      value={formData.repeatPerYear}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          repeatPerYear: e.target.value,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold small">
                      Sorumlu Personeller
                    </Form.Label>
                    <Select
                      isMulti
                      options={personels}
                      value={formData.responsibleUsers}
                      onChange={(selected) =>
                        setFormData({ ...formData, responsibleUsers: selected })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Label className="fw-semibold small mb-2">
                    Planlanan Aylar (Sadece yeni eklemede etkilidir)
                  </Form.Label>
                  <div className="month-grid mb-3">
                    {monthsList.map((m) => {
                      const isActive = formData.months.some(
                        (x) => x.month === m.id
                      );
                      return (
                        <div
                          key={m.id}
                          className={`month-box ${isActive ? "selected" : ""}`}
                          onClick={() => {
                            let updated = isActive
                              ? formData.months.filter((x) => x.month !== m.id)
                              : [
                                  ...formData.months,
                                  { month: m.id, status: "planned" },
                                ];
                            setFormData({ ...formData, months: updated });
                          }}
                        >
                          {m.name}
                        </div>
                      );
                    })}
                  </div>
                </Col>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-0 bg-light p-3">
            <Button
              variant="link"
              className="text-muted text-decoration-none"
              onClick={() => setShowPlanModal(false)}
            >
              İptal
            </Button>
            <Button variant="primary" className="px-4" onClick={handleSavePlan}>
              {editingPlan ? "Güncelle" : "Kaydet"}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* SABLON KALEMLERI YONETIMI MODALI (Admin) */}
        <Modal
          show={showTemplateModal}
          onHide={() => setShowTemplateModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title className="fw-bold fs-5 w-100">
              Checklist Şablonu
              <div className="text-muted small mt-1">
                {activePlanForTemplate?.title} İçin Sabit Kalemler
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4 bg-light">
            <InputGroup className="mb-4 shadow-sm">
              <Form.Control
                placeholder="Yeni kontrol kalemi (Örn: Lavabo temiz mi?)"
                value={newItemTitle}
                onChange={(e) => setNewItemTitle(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleAddItem()}
              />
              <Button variant="primary" onClick={handleAddItem}>
                Ekle
              </Button>
            </InputGroup>

            <ListGroup
              variant="flush"
              className="border rounded shadow-sm bg-white"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              {templateItems.length === 0 ? (
                <ListGroup.Item className="text-muted text-center py-3">
                  Henüz kalem eklenmemiş.
                </ListGroup.Item>
              ) : (
                templateItems.map((item, idx) => (
                  <ListGroup.Item
                    key={idx}
                    className="d-flex justify-content-between align-items-center py-3"
                  >
                    <span className="pe-3" style={{ wordBreak: "break-word" }}>
                      {item.title}
                    </span>
                    <div className="d-flex gap-2 shrink-0">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="btn-icon border-0"
                        onClick={() => handleEditItem(item)}
                      >
                        <Edit2 size={14} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="btn-icon border-0"
                        onClick={() => handleDeleteItem(item._id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </ListGroup.Item>
                ))
              )}
            </ListGroup>
          </Modal.Body>
        </Modal>

        {/* OPERASYON CHECKLIST MODALI (Kullanici) */}
        <Modal
          show={showChecklistModal}
          onHide={() => setShowChecklistModal(false)}
          centered
          backdrop="static"
        >
          <Modal.Header closeButton className="border-0 pb-0">
            <Modal.Title className="fw-bold fs-5 w-100">
              {monthsList.find((m) => m.id === activeTask.monthId)?.name} Ayı
              Denetimi
              {/* İSTATİSTİK BÖLÜMÜ BURAYA GELDİ */}
              <div className="d-flex gap-2 mt-2">
                <Badge
                  bg="success"
                  className="px-3 py-2 rounded-pill fs-6 d-flex align-items-center gap-1"
                >
                  <Check size={14} /> {checkedCount}
                </Badge>
                <Badge
                  bg="danger"
                  className="px-3 py-2 rounded-pill fs-6 d-flex align-items-center gap-1"
                >
                  <X size={14} /> {notDoneCount}
                </Badge>
                <Badge
                  bg="secondary"
                  className="px-3 py-2 rounded-pill fs-6 d-flex align-items-center gap-1"
                >
                  <Minus size={14} /> {naCount}
                </Badge>
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="p-4">
            {checklistItems.length === 0 ? (
              <div className="text-center text-muted py-4 bg-light rounded">
                <p className="mb-0">Bu plan için kontrol kalemi bulunamadı.</p>
                <small>Admin panelinden şablon eklenmesi gerekmektedir.</small>
              </div>
            ) : (
              <div className="mb-4 d-flex flex-column gap-2">
                {checklistItems.map((item) => (
                  // SATIRIN ARKA PLAN RENGİNİ STATUS'E GÖRE DEĞİŞTİRİYORUZ
                  <div
                    key={item._id}
                    className={`p-3 border rounded d-flex justify-content-between align-items-center ${
                      activeTask.status === "done" ? "opacity-75" : ""
                    } ${
                      item.status === "checked"
                        ? "bg-success bg-opacity-10 border-success"
                        : item.status === "not_done"
                        ? "bg-danger bg-opacity-10 border-danger"
                        : item.status === "not_applicable"
                        ? "bg-secondary bg-opacity-10 border-secondary"
                        : "bg-white"
                    }`}
                    style={{ transition: "0.2s" }}
                  >
                    <span
                      className={`fw-medium ${
                        item.status === "not_applicable"
                          ? "text-muted text-decoration-line-through"
                          : "text-dark"
                      }`}
                    >
                      {item.title}
                    </span>

                    {/* 3 DURUMLU BUTON GRUBU */}
                    <div className="d-flex gap-1">
                      <Button
                        variant={
                          item.status === "checked"
                            ? "success"
                            : "outline-success"
                        }
                        size="sm"
                        className="btn-icon"
                        disabled={activeTask.status === "done"}
                        onClick={() => handleStatusChange(item._id, "checked")}
                        title="Yapıldı"
                      >
                        <Check size={16} />
                      </Button>
                      <Button
                        variant={
                          item.status === "not_done"
                            ? "danger"
                            : "outline-danger"
                        }
                        size="sm"
                        className="btn-icon"
                        disabled={activeTask.status === "done"}
                        onClick={() => handleStatusChange(item._id, "not_done")}
                        title="Uygun Değil"
                      >
                        <X size={16} />
                      </Button>
                      <Button
                        variant={
                          item.status === "not_applicable"
                            ? "secondary"
                            : "outline-secondary"
                        }
                        size="sm"
                        className="btn-icon"
                        disabled={activeTask.status === "done"}
                        onClick={() =>
                          handleStatusChange(item._id, "not_applicable")
                        }
                        title="Uygulanamaz"
                      >
                        <Minus size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="d-flex flex-column gap-3 pt-3 border-top">
              {activeTask.status === "done" ? (
                <div className="d-flex justify-content-between align-items-center bg-success bg-opacity-10 p-3 rounded border border-success">
                  <div className="text-success small fw-bold d-flex align-items-center gap-2">
                    <Lock size={16} /> Bu ay kilitlendi.
                  </div>
                  {canUpdate && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={handleReset}
                      className="d-flex align-items-center gap-1"
                    >
                      <RotateCcw size={14} /> Kilidi Aç (Reset)
                    </Button>
                  )}
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleBulkCheck}
                    className="d-flex justify-content-center align-items-center gap-2"
                  >
                    <CheckSquare size={16} /> Tümünü Yapıldı İşaretle
                  </Button>

                  <Button
                    variant="success"
                    size="lg"
                    disabled={checklistItems.length === 0}
                    onClick={handleComplete}
                    className="w-100 fw-bold d-flex justify-content-center align-items-center gap-2 shadow-sm"
                  >
                    <CheckCircle size={20} /> Denetimi Tamamla
                  </Button>
                </div>
              )}
            </div>
          </Modal.Body>
        </Modal>
      </div>

      <style>{`
        .custom-table thead th { background-color: #f8f9fa; text-transform: uppercase; font-size: 11px; letter-spacing: 0.5px; color: #6c757d; border-bottom: 1px solid #eee; vertical-align: middle; }
        .table-month-indicator { width: 100%; min-height: 40px; padding: 4px 0; border-radius: 4px; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold; cursor: pointer; transition: all 0.2s; border: 1px solid transparent; overflow: hidden; }
        
        .month-box-empty { background: #f8f9fa; border-color: #e9ecef; color: transparent; }
        .month-box-empty:hover { background: #e2e6ea; }
        
        .month-box-planned { background: #fff3cd; color: #856404; border-color: #ffeeba; }
        .month-box-planned:hover { background: #ffe8a1; box-shadow: 0 0 5px rgba(255,193,7,0.4); }
        
        .month-box-done { background: #d1e7dd; color: #0f5132; border-color: #badbcc; }
        .month-box-done:hover { background: #bfe2d1; box-shadow: 0 0 5px rgba(25,135,84,0.4); }
        
        .month-box-not-done { background: #f8d7da; color: #842029; border-color: #f5c2c7; }
        
        .cursor-not-allowed { cursor: not-allowed !important; }
        .btn-icon { width: 32px; height: 32px; padding: 0; display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; }
        .month-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 10px; }
        .month-box { padding: 8px; border: 1px solid #dee2e6; border-radius: 6px; text-align: center; cursor: pointer; font-size: 13px; font-weight: 500; transition: 0.2s; background: #fff; color: #495057; }
        .month-box:hover { border-color: #0d6efd; background: #f8f9ff; }
        .month-box.selected { background: #0d6efd; color: white; border-color: #0d6efd; }
        .border-radius-15 { border-radius: 12px; overflow: hidden; }
        .shrink-0 { flex-shrink: 0; }
      `}</style>
    </Layout>
  );
}

export default HygienePlanPage;
